import {useMutation, useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

import type {TriagePath} from './utils/constants'

export type DebugPromptsPayload = {
  issue_number: number
  capi: {
    developer_prompt: string
    user_prompt: string
  }
  twirp: {
    problem_statement: string
  }
}

export type DebugSubmitPayload =
  | {
      issue_number: number
      path: 'capi'
      developer_prompt: string
      user_prompt: string
    }
  | {
      issue_number: number
      path: 'twirp'
      problem_statement: string
    }

export function useLoadDebugPrompts(repoOwner: string, repoName: string, issueNumber: number, enabled: boolean) {
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent/debug`

  return useQuery({
    queryKey: ['triage-agent-debug-prompts', apiUrl, issueNumber],
    queryFn: async () => {
      const response = await verifiedFetchJSON(`${apiUrl}?issue_number=${issueNumber}`)
      if (!response.ok) {
        throw new Error('Failed to fetch debug prompts')
      }
      return response.json() as Promise<DebugPromptsPayload>
    },
    enabled,
  })
}

export function useSubmitDebugTriage(repoOwner: string, repoName: string) {
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent/debug`

  return useMutation({
    mutationFn: async (payload: {
      issueNumber: number
      path: TriagePath
      developerPrompt?: string
      userPrompt?: string
      problemStatement?: string
    }) => {
      const body: DebugSubmitPayload =
        payload.path === 'capi'
          ? {
              issue_number: payload.issueNumber,
              path: 'capi',
              developer_prompt: payload.developerPrompt ?? '',
              user_prompt: payload.userPrompt ?? '',
            }
          : {
              issue_number: payload.issueNumber,
              path: 'twirp',
              problem_statement: payload.problemStatement ?? '',
            }

      const response = await verifiedFetchJSON(apiUrl, {
        method: 'POST',
        body,
      })
      if (!response.ok) {
        throw new Error('Failed to submit debug triage')
      }
      return response.json() as Promise<{status: string}>
    },
  })
}
